'use client'

import { useState, useEffect } from 'react'
import { useAccount, useWriteContract, useWaitForTransactionReceipt, useBlockNumber, useChainId } from 'wagmi'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { MULTISTAKING_CONTRACT_ADDRESS, MULTISTAKING_ABI } from '@/lib/contracts'
import { TransactionHash } from '@/components/ui/transaction-hash'
import { triggerWalletReminder } from '@/components/wallet-reminder-bot'
import { toast } from 'sonner'
import { blocksToTime, getBlockTimeDescription, getBlockTimeExamples } from '@/lib/block-times'

export function CreatePool() {
  const { address } = useAccount()
  const chainId = useChainId()
  const queryClient = useQueryClient()
  const [startBlock, setStartBlock] = useState('')
  const [endBlock, setEndBlock] = useState('')
  const [rewardMultiplier, setRewardMultiplier] = useState('15000') // 1.5x default

  // Get current block for suggestions
  const { data: currentBlock } = useBlockNumber({
    watch: true, // Auto-refresh to stay in sync
  })

  const { data: hash, writeContract, isPending } = useWriteContract()

  const { isLoading: isConfirming, isSuccess: isConfirmed } = 
    useWaitForTransactionReceipt({ hash })

  // Handle pool creation success
  useEffect(() => {
    if (isConfirmed) {
      toast.success('Pool created successfully!', { id: 'create-pool' })
      // Invalidate all queries with scopeKey 'pools'
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey as any[]
          return key[0] === 'readContract' && 
                 key[1]?.scopeKey === 'pools'
        }
      })
      // Reset form
      setStartBlock('')
      setEndBlock('')
      setRewardMultiplier('15000')
    }
  }, [isConfirmed, queryClient])

  // Helper functions for better UX
  const suggestStartBlock = () => {
    if (currentBlock) {
      const suggested = Number(currentBlock) + 100
      setStartBlock(suggested.toString())
    }
  }

  const suggestEndBlock = () => {
    if (currentBlock) {
      const suggested = Number(currentBlock) + 1100 // ~3-4 hours
      setEndBlock(suggested.toString())
    }
  }

  const setPresetMultiplier = (multiplier: number) => {
    setRewardMultiplier(multiplier.toString())
  }

  const calculateDuration = () => {
    if (startBlock && endBlock) {
      const blocks = parseInt(endBlock) - parseInt(startBlock)
      const time = blocksToTime(blocks, chainId)
      return {
        blocks,
        formatted: time.formatted,
        hours: Math.round(time.totalSeconds / 3600 * 10) / 10
      }
    }
    return null
  }

  const getMultiplierDescription = () => {
    if (!rewardMultiplier) return ''
    const multiplier = parseInt(rewardMultiplier) / 10000
    const bonus = ((multiplier - 1) * 100).toFixed(0)
    return `${multiplier.toFixed(2)}x rewards (${bonus}% bonus)`
  }

  const handleCreatePool = async () => {
    if (!startBlock || !endBlock || !rewardMultiplier) {
      toast.error('Please fill in all fields')
      return
    }

    if (parseInt(startBlock) >= parseInt(endBlock)) {
      toast.error('End block must be greater than start block')
      return
    }

    if (parseInt(rewardMultiplier) < 10000) {
      toast.error('Reward multiplier must be at least 1.0x (10000)')
      return
    }

    if (currentBlock && parseInt(startBlock) <= Number(currentBlock)) {
      toast.error('Start block must be in the future')
      return
    }

    try {
      writeContract({
        address: MULTISTAKING_CONTRACT_ADDRESS,
        abi: MULTISTAKING_ABI,
        functionName: 'createPool',
        args: [parseInt(startBlock), parseInt(endBlock), BigInt(rewardMultiplier)],
      })
      toast.loading('Creating pool...', { id: 'create-pool' })
    } catch (err) {
      console.error('Error creating pool:', err)
      toast.error('Failed to create pool')
    }
  }


  const duration = calculateDuration()
  const isFormValid = startBlock && endBlock && rewardMultiplier && 
                     parseInt(endBlock) > parseInt(startBlock) &&
                     parseInt(rewardMultiplier) >= 10000 &&
                     (!currentBlock || parseInt(startBlock) > Number(currentBlock))

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-mono text-green-600 mb-2">🏗️ Create New Pool</h2>
        <p className="text-sm text-green-700/80 font-mono leading-relaxed">
          Create a custom staking pool with your own parameters. Only contract owners can create pools.
        </p>
      </div>

      {/* Owner Warning */}
      <div className="bg-yellow-900/10 border border-yellow-700/30 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-yellow-500">⚠️</span>
          <span className="font-mono text-yellow-600 font-medium">Owner Only Feature</span>
        </div>
        <p className="text-xs text-yellow-600/80 font-mono">
          Only the contract owner can create pools. Make sure the contract has enough AMIRA tokens to pay rewards.
        </p>
      </div>

      <div className="bg-black border border-green-700/30 rounded-lg p-6 space-y-6">
        {/* Current Block Info */}
        {currentBlock && (
          <div className="bg-green-900/10 border border-green-700/20 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-green-600/70 text-xs font-mono uppercase">Current Block</span>
                <p className="text-green-500 font-mono text-lg font-bold">{currentBlock.toString()}</p>
              </div>
              <Badge variant="outline" className="border-green-700/50 text-green-600 text-xs font-mono">
                Live
              </Badge>
            </div>
          </div>
        )}

        {/* Block Range Configuration */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-green-600 text-lg">⏰</span>
            <h3 className="text-lg font-mono text-green-600">Pool Duration</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Start Block */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="startBlock" className="text-green-600/80 text-sm font-mono font-medium">
                  🚀 Start Block
                </Label>
                <Button 
                  onClick={suggestStartBlock}
                  variant="outline" 
                  size="sm"
                  className="border-green-700/50 text-green-600 hover:bg-green-800/10 text-xs font-mono"
                >
                  Suggest
                </Button>
              </div>
              <Input
                id="startBlock"
                type="number"
                placeholder={currentBlock ? (Number(currentBlock) + 100).toString() : "Block number"}
                value={startBlock}
                onChange={(e) => setStartBlock(e.target.value)}
                className="bg-black/80 border-green-700/50 text-green-400 font-mono text-lg placeholder-green-600/40 focus:border-green-500 focus:ring-1 focus:ring-green-500/30"
              />
              <div className="text-xs text-green-600/60 font-mono">
                When staking opens (future block)
              </div>
            </div>
            
            {/* End Block */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="endBlock" className="text-green-600/80 text-sm font-mono font-medium">
                  🏁 End Block
                </Label>
                <Button 
                  onClick={suggestEndBlock}
                  variant="outline" 
                  size="sm"
                  className="border-green-700/50 text-green-600 hover:bg-green-800/10 text-xs font-mono"
                >
                  Suggest
                </Button>
              </div>
              <Input
                id="endBlock"
                type="number"
                placeholder={currentBlock ? (Number(currentBlock) + 1100).toString() : "Block number"}
                value={endBlock}
                onChange={(e) => setEndBlock(e.target.value)}
                className="bg-black/80 border-green-700/50 text-green-400 font-mono text-lg placeholder-green-600/40 focus:border-green-500 focus:ring-1 focus:ring-green-500/30"
              />
              <div className="text-xs text-green-600/60 font-mono">
                When rewards stop (must be after start)
              </div>
            </div>
          </div>

          {/* Duration Display */}
          {duration && (
            <div className="bg-blue-900/10 border border-blue-700/20 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-blue-600/70 text-xs font-mono uppercase">Pool Duration</span>
                <div className="text-right">
                  <p className="text-blue-400 font-mono font-bold">{duration.blocks.toLocaleString()} blocks</p>
                  <p className="text-blue-500/80 text-xs font-mono">≈ {duration.formatted}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <Separator className="bg-green-700/20" />

        {/* Reward Multiplier Configuration */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-green-600 text-lg">💰</span>
            <h3 className="text-lg font-mono text-green-600">Reward Configuration</h3>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="rewardMultiplier" className="text-green-600/80 text-sm font-mono font-medium">
                Reward Multiplier
              </Label>
              <Badge variant="outline" className="border-green-700/50 text-green-600 text-xs font-mono">
                {getMultiplierDescription()}
              </Badge>
            </div>
            <Input
              id="rewardMultiplier"
              type="number"
              placeholder="e.g., 15000"
              value={rewardMultiplier}
              onChange={(e) => setRewardMultiplier(e.target.value)}
              className="bg-black/80 border-green-700/50 text-green-400 font-mono text-lg placeholder-green-600/40 focus:border-green-500 focus:ring-1 focus:ring-green-500/30"
            />
            <div className="text-xs text-green-600/60 font-mono">
              10000 = 1.0x (no bonus), 15000 = 1.5x (50% bonus)
            </div>
          </div>

          {/* Preset Multipliers */}
          <div className="space-y-2">
            <span className="text-green-600/70 text-xs font-mono uppercase">Quick Presets</span>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 10000, label: '1.0x (No Bonus)' },
                { value: 12000, label: '1.2x (20% Bonus)' },
                { value: 15000, label: '1.5x (50% Bonus)' },
                { value: 20000, label: '2.0x (100% Bonus)' },
              ].map((preset) => (
                <Button
                  key={preset.value}
                  onClick={() => setPresetMultiplier(preset.value)}
                  variant="outline"
                  size="sm"
                  className={`border-green-700/50 text-green-600 hover:bg-green-800/10 text-xs font-mono ${
                    rewardMultiplier === preset.value.toString() 
                      ? 'bg-green-800/20 border-green-600' 
                      : ''
                  }`}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <Separator className="bg-green-700/20" />

        {/* Form Validation Display */}
        <div className="space-y-2">
          <span className="text-green-600/70 text-xs font-mono uppercase">Form Validation</span>
          <div className="space-y-1">
            <div className={`flex items-center gap-2 text-xs font-mono ${
              address ? 'text-green-500' : 'text-red-500'
            }`}>
              {address ? '✅' : '❌'}
              Wallet connected
            </div>
            <div className={`flex items-center gap-2 text-xs font-mono ${
              startBlock ? 'text-green-500' : 'text-red-500'
            }`}>
              {startBlock ? '✅' : '❌'}
              Start block entered ({startBlock || 'empty'})
            </div>
            <div className={`flex items-center gap-2 text-xs font-mono ${
              endBlock ? 'text-green-500' : 'text-red-500'
            }`}>
              {endBlock ? '✅' : '❌'}
              End block entered ({endBlock || 'empty'})
            </div>
            <div className={`flex items-center gap-2 text-xs font-mono ${
              rewardMultiplier ? 'text-green-500' : 'text-red-500'
            }`}>
              {rewardMultiplier ? '✅' : '❌'}
              Reward multiplier entered ({rewardMultiplier || 'empty'})
            </div>
            <div className={`flex items-center gap-2 text-xs font-mono ${
              startBlock && endBlock && parseInt(endBlock) > parseInt(startBlock) ? 'text-green-500' : 'text-red-500'
            }`}>
              {startBlock && endBlock && parseInt(endBlock) > parseInt(startBlock) ? '✅' : '❌'}
              End block is after start block
            </div>
            <div className={`flex items-center gap-2 text-xs font-mono ${
              rewardMultiplier && parseInt(rewardMultiplier) >= 10000 ? 'text-green-500' : 'text-red-500'
            }`}>
              {rewardMultiplier && parseInt(rewardMultiplier) >= 10000 ? '✅' : '❌'}
              Reward multiplier is valid (≥ 1.0x)
            </div>
            <div className={`flex items-center gap-2 text-xs font-mono ${
              !currentBlock || (startBlock && parseInt(startBlock) > Number(currentBlock)) ? 'text-green-500' : 'text-red-500'
            }`}>
              {!currentBlock || (startBlock && parseInt(startBlock) > Number(currentBlock)) ? '✅' : '❌'}
              Start block is in the future (current: {currentBlock?.toString() || 'loading'})
            </div>
          </div>
          
          {/* Debug Info */}
          <div className="mt-3 p-2 bg-gray-900/20 border border-gray-700/30 rounded text-xs font-mono">
            <div className="text-gray-400 mb-1">Debug Info:</div>
            <div className="text-gray-300">
              isFormValid: {isFormValid ? 'true' : 'false'} | 
              isPending: {isPending ? 'true' : 'false'} | 
              isConfirming: {isConfirming ? 'true' : 'false'}
            </div>
          </div>
        </div>

        {/* Create Button */}
        <Button 
          onClick={address ? handleCreatePool : () => triggerWalletReminder("You need to connect your wallet to create a pool! 🏗️")}
          disabled={isPending || isConfirming || (address && !isFormValid)}
          className={`w-full font-mono font-medium text-lg py-6 ${
            (!address || isFormValid)
              ? 'bg-green-800/30 border border-green-600/50 text-green-400 hover:bg-green-700/40 hover:text-green-300'
              : 'bg-gray-800/20 border border-gray-600/30 text-gray-500'
          } disabled:opacity-50`}
        >
          {isPending || isConfirming ? (
            <span className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin"></div>
              Creating Pool...
            </span>
          ) : isConfirmed ? (
            <span className="flex items-center gap-2">
              🎉 Pool Created Successfully!
            </span>
          ) : (
            '🚀 Create Staking Pool'
          )}
        </Button>

        {hash && (
          <TransactionHash hash={hash} label="Create Pool Transaction" />
        )}
      </div>

      {/* Help Section */}
      <div className="bg-green-900/5 border border-green-700/20 rounded-lg p-4">
        <h4 className="font-mono text-green-600 font-medium mb-2">💡 Pool Creation Tips</h4>
        <ul className="text-xs text-green-600/70 font-mono space-y-1">
          <li>• Ensure contract has enough AMIRA tokens for rewards</li>
          <li>• Users can only stake during "Open" status (before start block)</li>
          <li>• Pool earns rewards during "Running" status (start to end block)</li>
          <li>• Users can withdraw after "Finished" status (after end block)</li>
          <li>• {getBlockTimeDescription(chainId)}: {getBlockTimeExamples(chainId)[1]}</li>
        </ul>
      </div>
    </div>
  )
} 
 